import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch, { SwitchProps } from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import React from 'react'
import { TimeZoneSelect } from '../selection/TimeZoneSelect'
import { ISOTimePicker } from '../util/ISOPickers'
import { AlertSeverity, ContactMethodType, WeekdayFilter } from '../../schema'
import { FormContainer, FormField } from '../forms'
import { FieldError } from '../util/errutil'
import Checkbox from '@mui/material/Checkbox'
import makeStyles from '@mui/styles/makeStyles'

interface ServiceNotificationRuleFormValue {
  delayMinutes: number
  contactMethodType: ContactMethodType
  alertSeverity: AlertSeverity
  notifyAnytime: boolean
  startTime: string
  endTime: string
  timezone: string
  weekdayFilter: WeekdayFilter
}

interface ServiceNotificationRuleFormProps {
  value: ServiceNotificationRuleFormValue
  errors: FieldError[]
  onChange: (value: ServiceNotificationRuleFormValue) => void
  disabled: boolean
}

const contactMethodTypes: ContactMethodType[] = ['VOICE', 'SMS', 'EMAIL']

const alertSeverities: AlertSeverity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']

const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const useStyles = makeStyles({
  margin0: { margin: 0 },
})

function SwitchWithLabel(props: SwitchProps & { content: string }): JSX.Element {
  const { content, ...switchProps } = props
  return (
    <FormControlLabel control={<Switch {...switchProps} />} label={content} />
  )
}

export default function ServiceNotificationRuleForm(
  props: ServiceNotificationRuleFormProps,
): JSX.Element {
  const classes = useStyles()
  return (
    <FormContainer {...props} optionalLabels>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormField
            fullWidth
            name='contactMethodType'
            label='Contact Method Type'
            required
            select
            component={TextField}
          >
            {contactMethodTypes.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </FormField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormField
            fullWidth
            name='alertSeverity'
            label='Alert Severity'
            required
            select
            component={TextField}
          >
            {alertSeverities.map((s) => (
              <MenuItem key={s} value={s}>
                {s.toUpperCase()}
              </MenuItem>
            ))}
          </FormField>
        </Grid>
        <Grid item xs={12}>
          <FormField
            fullWidth
            name='delayMinutes'
            required
            label='Delay (minutes)'
            type='number'
            min={0}
            max={9000}
            component={TextField}
          />
        </Grid>
        <Grid item xs={12}>
          <FormField
            component={SwitchWithLabel}
            // @ts-expect-error: content prop is passed through to SwitchWithLabel
            content='Notify Anytime'
            name='notifyAnytime'
            fieldName='notifyAnytime'
            checkbox
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container justifyContent='space-between'>
            {days.map((day, i) => (
              <FormControlLabel
                key={i}
                label={day}
                labelPlacement='top'
                classes={{ labelPlacementTop: classes.margin0 }}
                control={
                  <FormField
                    noError
                    component={Checkbox}
                    checkbox
                    name={`weekdayFilter[${i}]`}
                  />
                }
              />
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FormField
            fullWidth
            name='timezone'
            required
            label='Time Zone'
            component={TimeZoneSelect}
          />
        </Grid>
        {!props.value.notifyAnytime && (
          <React.Fragment>
            <Grid item xs={6}>
              <FormField
                fullWidth
                name='startTime'
                required
                label='Start Time'
                component={ISOTimePicker}
                timeZone={props.value.timezone}
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                fullWidth
                name='endTime'
                required
                label='End Time'
                component={ISOTimePicker}
                timeZone={props.value.timezone}
              />
            </Grid>
          </React.Fragment>
        )}
      </Grid>
    </FormContainer>
  )
}
